<template>
  <AssignedUserContainer
    :id="id"
    :model-value="internalValue"
    :options="assignableUsers"
    :disabled="!authorization.hasAuthority(type, 'update')"
    icon-button-only
    @update:model-value="saveAssignedUsers"
  >
    <template #buttonText>
      {{ t('common.assignParam', { param: t('users.users', 2) }) }}
    </template>
  </AssignedUserContainer>
</template>

<script lang="ts" setup>
import axios from 'axios';
import type { PropType, Ref } from 'vue';
import { onMounted, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { useModelValue, useToast } from '@rygen/rygen-ui/composables';
import { Loadable, UserRef } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import api from '@/api';
import { useAssignableUserStore } from '@/stores/AssignableUser';

import AssignedUserContainer from '@rygen/rygen-ui/components/form/AssignedUserContainer.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Array as PropType<UserRef[]>,
    default: () => [],
  },
  parentId: {
    type: Number,
    default: () => 0,
  },
  ownerId: {
    type: Number,
    default: () => 0,
  },
  type: {
    type: String as PropType<'order' | 'shipment'>,
    default: 'order' as const,
  },
});
const { id, modelValue, parentId, ownerId, type } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: UserRef[]): void;
}>();

const { loadAssignableUsersByDomainId } = useAssignableUserStore();
const { t } = useI18n();
const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod: (opts: Partial<UserRef[]>) => opts.filter(notNull),
});
const toast = useToast();

const assignableUsers: Ref<Loadable<UserRef[]>> = ref({ loading: false });

const saveAssignedUsers = async (assignedUsers: UserRef[]) => {
  if (!parentId.value) {
    return;
  }

  try {
    const result =
      type.value === 'order'
        ? await api.order.updateAssignedUsers(parentId.value, assignedUsers)
        : await api.shipment.updateAssignedUsers(parentId.value, assignedUsers);

    toast.addSuccess({
      detail: t('messages.successSavingParam', {
        param: t('users.users', 2),
      }),
    });
    internalValue.value = result.assignedUsers;
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    toast.addError({
      detail: t('errors.savingParam', {
        param: t('users.users', 2),
      }),
      error: e,
    });
  }
};

onMounted(() => {
  assignableUsers.value = loadAssignableUsersByDomainId(
    ownerId.value,
    type.value,
  );
});
</script>
